/**
 * parseOutfit — outfit_json / swim_outfit_json → warstwy renderu (R15).
 * Czysta funkcja (bez Phaser) — unit-testowalna.
 */
import {
  ITEMS,
  type OutfitSlot,
  type SwimSlot,
} from '@pieskowo/shared';

/** 7 kategorii stroju dziennego — z-order od dołu (buty) do góry (dodatek). */
export const OUTFIT_Z_ORDER = [
  'shoes',
  'body',
  'collar',
  'glasses',
  'bow',
  'hat',
  'accessory',
] as const satisfies readonly OutfitSlot[];

/** Warstwy stroju kąpielowego (basen R31). */
export const SWIM_Z_ORDER = ['suit', 'cap'] as const satisfies readonly SwimSlot[];

export type OutfitSlots = Record<OutfitSlot, string | null>;
export type SwimSlots = Record<SwimSlot, string | null>;

export type AvatarLayer = {
  /** Slot kategorii lub 'base' dla sylwetki pieska. */
  slot: OutfitSlot | SwimSlot | 'base';
  /** Id itemu z katalogu shared/items, null = pusto. */
  itemId: string | null;
  /** Klucz tekstury (manifest WP13) — null gdy brak itemu. */
  textureKey: string | null;
  /** Kolejność rysowania (0 = pod spodem). */
  zIndex: number;
};

export type ParseOutfitInput = {
  /** outfit_json — string JSON lub obiekt slotów. */
  outfit?: string | Record<string, unknown> | null;
  /** swim_outfit_json — string JSON lub obiekt. */
  swim?: string | Record<string, unknown> | null;
  /** tryb renderu: normal = 7 warstw, swim = suit+cap. */
  mode?: 'normal' | 'swim';
  /** Klucz tekstury bazy pieska (placeholder / rasa). */
  baseTextureKey?: string;
};

const EMPTY_OUTFIT: OutfitSlots = {
  body: null,
  hat: null,
  bow: null,
  glasses: null,
  collar: null,
  shoes: null,
  accessory: null,
};

const EMPTY_SWIM: SwimSlots = {
  suit: null,
  cap: null,
};

const KNOWN_ITEM_IDS = new Set(ITEMS.map((i) => i.id));

function parseJsonObject(
  raw: string | Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw !== 'string') return {};
  const t = raw.trim();
  if (!t) return {};
  try {
    const v = JSON.parse(t) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return v as Record<string, unknown>;
    }
    return {};
  } catch {
    return {};
  }
}

function slotValue(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  if (v == null) return null;
  if (typeof v !== 'string') return null;
  const id = v.trim();
  if (!id) return null;
  // nieznane id → null (bezpieczny fallback, nie crashuje renderu)
  if (!KNOWN_ITEM_IDS.has(id)) return null;
  return id;
}

/** Znormalizuj outfit_json → 7 slotów. */
export function normalizeOutfitSlots(
  raw: string | Record<string, unknown> | null | undefined,
): OutfitSlots {
  const obj = parseJsonObject(raw);
  const out = { ...EMPTY_OUTFIT };
  for (const slot of OUTFIT_Z_ORDER) {
    out[slot] = slotValue(obj, slot);
  }
  return out;
}

/** Znormalizuj swim_outfit_json → suit/cap. */
export function normalizeSwimSlots(
  raw: string | Record<string, unknown> | null | undefined,
): SwimSlots {
  const obj = parseJsonObject(raw);
  return {
    suit: slotValue(obj, 'suit'),
    cap: slotValue(obj, 'cap'),
  };
}

/**
 * parseOutfit(outfit_json) → warstwy z z-order.
 * Zawsze zwraca warstwę `base` + sloty aktywnego trybu.
 */
export function parseOutfit(input: ParseOutfitInput = {}): AvatarLayer[] {
  const mode = input.mode ?? 'normal';
  const baseKey = input.baseTextureKey ?? 'ph_dog';
  const layers: AvatarLayer[] = [
    {
      slot: 'base',
      itemId: null,
      textureKey: baseKey,
      zIndex: 0,
    },
  ];

  if (mode === 'swim') {
    const swim = normalizeSwimSlots(input.swim ?? input.outfit);
    SWIM_Z_ORDER.forEach((slot, i) => {
      const itemId = swim[slot];
      layers.push({
        slot,
        itemId,
        textureKey: itemId ? itemTextureKey(itemId) : null,
        zIndex: i + 1,
      });
    });
    return layers;
  }

  const slots = normalizeOutfitSlots(input.outfit);
  OUTFIT_Z_ORDER.forEach((slot, i) => {
    const itemId = slots[slot];
    layers.push({
      slot,
      itemId,
      textureKey: itemId ? itemTextureKey(itemId) : null,
      zIndex: i + 1,
    });
  });
  return layers;
}

/** Klucz assetu ubranka — WP13 dostarczy atlas pod tym id (lub placeholder). */
export function itemTextureKey(itemId: string): string {
  return `item_${itemId}`;
}

/** Pusta szafa (wszystkie sloty null). */
export function emptyOutfitSlots(): OutfitSlots {
  return { ...EMPTY_OUTFIT };
}

export function emptySwimSlots(): SwimSlots {
  return { ...EMPTY_SWIM };
}

/** Serializacja do outfit_json / body PUT. */
export function serializeOutfitSlots(slots: OutfitSlots): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const s of OUTFIT_Z_ORDER) {
    out[s] = slots[s];
  }
  return out;
}

export function serializeSwimSlots(slots: SwimSlots): Record<string, string | null> {
  return { suit: slots.suit, cap: slots.cap };
}
